import { Link } from 'react-router-dom';

import { Chip, IconButton } from '@mui/material';
import {
  getGridDateOperators,
  getGridStringOperators,
  GridColDef,
} from '@mui/x-data-grid-pro';
import moment from 'moment';

import { IcDotSpread } from '@/assets/images';
import { excludeValues } from '@/components/DataGrid/utils';
import { DATE_TIME_FORMAT } from '@/config';
import { colors } from '@/theme/variables';
import { formatPersonName } from '@/utils';

const linkCellStyle = {
  textDecoration: 'none',
  color: 'inherit',
  display: 'block',
  width: '100%',
};

const linkTextStyle = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
  width: '100%',
};

const renderLinkCell = (to: string, value: string) => {
  const search = window.location.search;
  return (
    <Link to={`${to}${search}`} style={linkCellStyle}>
      <span style={linkTextStyle}>{value}</span>
    </Link>
  );
};

const taskID = (): Partial<GridColDef> => ({
  renderCell: (params) =>
    renderLinkCell(`/tasks/${params.row._id}/view`, params.row.taskID || '-'),
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty']),
  ),
});

const title = (): Partial<GridColDef> => ({
  renderCell: (params) =>
    renderLinkCell(`/tasks/${params.row._id}/view`, params.row.title || '-'),
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty']),
  ),
});

const taskType = (): Partial<GridColDef> => ({
  renderCell: (params) =>
    renderLinkCell(
      `/tasks/${params.row._id}/view`,
      params.row.taskType?.type || '-',
    ),
  field: 'taskType.type',
  valueGetter: (_, row) => row.taskType?.type,
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty']),
  ),
});

const formatAssignees = (
  assignees?: { fullName?: string; firstName?: string; lastName?: string }[],
) => {
  if (!assignees?.length) return '-';
  const names = assignees
    .map((assignee) => formatPersonName(assignee))
    .filter(Boolean);
  return names.length ? names.join(', ') : '-';
};

const assignees = (): Partial<GridColDef> => ({
  renderCell: (params) =>
    renderLinkCell(
      `/tasks/${params.row._id}/view`,
      formatAssignees(params.row.assignees),
    ),
  field: 'assignees.fullName',
  valueGetter: (_, row) => formatAssignees(row.assignees),
});

const createdAt = (): Partial<GridColDef> => ({
  renderCell: (params) =>
    renderLinkCell(
      `/tasks/${params.row._id}/view`,
      params.row.createdAt
        ? moment(params.row.createdAt).format(DATE_TIME_FORMAT)
        : '-',
    ),
  filterOperators: getGridDateOperators(true).filter(
    excludeValues(['isNotEmpty', 'isEmpty']),
  ),
});

const actions = (
  openMenu?: (event: React.MouseEvent<HTMLButtonElement>, param: any) => void,
): Partial<GridColDef> => ({
  sortable: false,
  filterable: false,
  renderCell: (params) => (
    <IconButton
      onClick={(e) => {
        e.stopPropagation();
        openMenu?.(e, params.row);
      }}
    >
      <IcDotSpread />
    </IconButton>
  ),
});

const status = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    const statusColor = params.row.status?.bgColorHex;
    return (
      <Link
        to={`/tasks/${params.row._id}/view${window.location.search}`}
        style={{
          textDecoration: 'none',
          color: 'inherit',
          display: 'flex',
          width: '100%',
          height: '100%',
          alignItems: 'center',
        }}
      >
        <Chip
          label={params.row.status?.status || '-'}
          sx={{
            backgroundColor: statusColor || colors.primary.main,
            color: colors.white,
            fontWeight: 500,
            fontSize: '12px',
            height: '25px',
            borderRadius: '6px',
          }}
        />
      </Link>
    );
  },
  valueGetter: (_, row) => row.status?.status || '-',
  field: 'status.status',
});

export const taskCols = (
  openMenu?: (event: React.MouseEvent<HTMLButtonElement>, param: any) => void,
) => ({
  taskID,
  title,
  taskType,
  status,
  assignees,
  createdAt,
  actions: () => actions(openMenu),
});
